import { IAddressCollection, IBasicAddress, ICheckoutDeliveryResponseWithData, IGuestCustomer, IInvoiceAddress, IOnlinePaymentProvider, IOrderDetailsResponse, TTransactionMethods, TTransactionType, TaxReceiptUsageType, TaxRegimeType, ISelectedShop } from '@features/checkout/typescript';
import NextCheckoutApi from '@features/api/nextApiService/nextCheckoutApi';
import NextCustomerApi from '@features/api/nextApiService/nextCustomerApi';
import { DICTIONARY_TAX_REGIME_TYPE } from '@features/checkout/const';
import { LOG_ERROR } from '@const/index';
import { logToSentry } from '@hooks/useSentryLogger';
import fetchNext from '@features/api/fetchNext';
import { IAdditionalArgument, IAdditionalArguments, IAdditionalArgumentsRecord, ICartAvailableDelivery, TAdditionalDeliveryAddressParameter } from '@features/cart/typescript';
import { DELIVERY_PACKAGE_IS_OVERWEIGHT } from '@features/checkout/const/error';
const fetchCheckoutAddressCollection = async (customerID: string): Promise<IAddressCollection | null> => {
  try {
    const {
      success,
      data
    } = await NextCheckoutApi.getCheckoutAddressCollection();
    if (!success || !data) return null;
    const addressIds = [data.basicAddressID, data.deliveryAddressID, data.invoiceAddressID].filter(id => id !== undefined);
    if (addressIds.length > 0) {
      const customerAddress = await NextCustomerApi.getAddressesCollection(addressIds, customerID);
      return {
        basicAddress: customerAddress[data.basicAddressID],
        deliveryAddress: customerAddress[data.deliveryAddressID],
        invoiceAddress: customerAddress[data.invoiceAddressID]
      };
    }
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0001]`, JSON.stringify(e));
  }
  return null;
};
const loadAddresses = async (locale: string): Promise<Array<IBasicAddress>> => {
  try {
    const data = await fetchNext('customer/addresses', {
      method: 'GET'
    }, locale);
    return data.json;
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0002]`, JSON.stringify(e));
    return [];
  }
};
const createOrder = async (cartId: string, customerId: string) => {
  try {
    const response = await NextCheckoutApi.createOrder(cartId, customerId);
    return {
      success: response.success,
      message: JSON.stringify(response)
    };
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0003]`, JSON.stringify(e));
    return {
      success: false,
      message: JSON.stringify(e)
    };
  }
};
const createCustomerAccountWithAddress = async (addressCollection: IAddressCollection) => {
  try {
    return await NextCustomerApi.changeAndCreateAddresses(addressCollection);
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0004]`, JSON.stringify(e));
    return null;
  }
};
const createGuestAccountWithAddress = async (guestCustomer: IGuestCustomer) => {
  try {
    return await NextCustomerApi.createGuestAccountWithAddress(guestCustomer);
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0005]`, JSON.stringify(e));
    return null;
  }
};
const getTaxReceiptUsage = async (): Promise<Array<TaxReceiptUsageType>> => {
  try {
    const data = await NextCheckoutApi.getAvailableDigitalTaxReceiptUsage();
    return data.map(({
      digitalTaxReceiptUsageId,
      digitalTaxReceiptUsage
    }) => ({
      value: digitalTaxReceiptUsageId,
      name: digitalTaxReceiptUsage
    }));
  } catch {
    return [];
  }
};
const getTaxRegime = async (): Promise<Array<TaxRegimeType>> => {
  try {
    const data = await NextCustomerApi.getDictionaryType(DICTIONARY_TAX_REGIME_TYPE);
    return data.map(({
      description,
      code
    }) => ({
      value: code,
      name: description
    }));
  } catch {
    return [];
  }
};
const getOrderDetails = async (): Promise<IOrderDetailsResponse | null> => {
  try {
    const response = await NextCheckoutApi.getOrderDetails();
    return {
      success: response.success,
      data: response.data
    };
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0006]`, JSON.stringify(error));
    return null;
  }
};
const getAvailableDeliveries = async (cartId: string, transportGroupId: string, additionalArguments?: IAdditionalArgument | IAdditionalArguments | IAdditionalArgumentsRecord): Promise<ICheckoutDeliveryResponseWithData> => {
  try {
    const deliveriesResponse = await NextCheckoutApi.getAvailableDeliveries(cartId, transportGroupId, additionalArguments);
    if ('error' in deliveriesResponse) {
      if ('json' in deliveriesResponse) {
        const {
          json
        } = deliveriesResponse;
        if (json && typeof json === 'object' && 'errors' in json) {
          const {
            errors
          } = json;
          if (errors && Array.isArray(errors)) {
            const overWeightError = errors.find(error => error.extensions?.codename === DELIVERY_PACKAGE_IS_OVERWEIGHT);
            if (overWeightError) {
              return {
                success: false,
                data: [],
                message: DELIVERY_PACKAGE_IS_OVERWEIGHT
              };
            }
          }
        }
      }
      throw new Error(deliveriesResponse.message);
    }
    return {
      success: deliveriesResponse.success,
      data: deliveriesResponse.data,
      services: deliveriesResponse.services,
      servicesPool: deliveriesResponse.servicesPool
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      message: JSON.stringify(error)
    };
  }
};
const getTransactionMethods = async (): Promise<TTransactionMethods | null> => {
  try {
    return await NextCheckoutApi.getTransactionMethods();
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0008]`, JSON.stringify(error));
    return null;
  }
};
const changeDelivery = async (cartId: string, delivery: ICartAvailableDelivery, transportGroupId: string, additionalDeliveryAddressParameters?: TAdditionalDeliveryAddressParameter) => {
  try {
    return await NextCheckoutApi.changeDelivery(cartId, delivery, transportGroupId, additionalDeliveryAddressParameters);
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0009]`, JSON.stringify(error));
    return null;
  }
};
const setCheckoutAddressCollection = async (basicAddressID: number, deliveryAddressID: number | null, invoiceAddressID: number | null) => {
  try {
    return await NextCheckoutApi.setCheckoutAddressCollection({
      basicAddressID,
      deliveryAddressID,
      invoiceAddressID
    });
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0010]`, JSON.stringify(error));
    return null;
  }
};
const setSelectedDigitalTaxReceiptUsageDataId = async (availableDigitalTaxReceiptUsage: string) => {
  try {
    return await NextCheckoutApi.setSelectedDigitalTaxReceiptUsageDataId(availableDigitalTaxReceiptUsage);
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0011]`, JSON.stringify(error));
    return null;
  }
};
const setSelectedTaxRegimeDataId = async (taxRegime: string) => {
  try {
    return await NextCheckoutApi.setSelectedTaxRegimeDataId(taxRegime);
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0012]`, JSON.stringify(error));
    return null;
  }
};
const setSelectedPayment = async (provider: IOnlinePaymentProvider) => {
  try {
    return await NextCheckoutApi.setSelectedPayment(provider);
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0021]`, JSON.stringify(error));
    return null;
  }
};
const clearSessionOrderId = async () => {
  try {
    return await NextCheckoutApi.clearSessionOrderId();
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0023]`, JSON.stringify(error));
    return null;
  }
};
const setSelectedShop = async (shop: ISelectedShop | null) => {
  try {
    return await NextCheckoutApi.setSelectedShop(shop);
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0022]`, JSON.stringify(error));
    return null;
  }
};
const assignCustomerToCart = async (cartId: string, customerId: string) => {
  try {
    return await NextCheckoutApi.assignCustomerToCart(cartId, customerId);
  } catch (error) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0014]`, JSON.stringify(error));
    return null;
  }
};
const changeAddress = async (addressId: number, address: IBasicAddress | IInvoiceAddress): Promise<IBasicAddress | null> => {
  try {
    return await NextCustomerApi.changeAddress(addressId, address);
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0016]`, JSON.stringify(e));
    return null;
  }
};
const createAddress = async (address: IBasicAddress): Promise<IBasicAddress | null> => {
  try {
    return await NextCustomerApi.createAddress(address);
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0017]`, JSON.stringify(e));
    return null;
  }
};
const deleteAddress = async (addressId: number) => {
  try {
    await NextCustomerApi.deleteUserAddress(addressId);
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0018]`, JSON.stringify(e));
  }
};
const createTransaction = async (customerId: string, orderId: string, locale: string, paymentProvider: IOnlinePaymentProvider, amount: number, transactionType: TTransactionType = 'ONLINE_CHECKOUT') => {
  try {
    const response = await NextCustomerApi.createTransaction(customerId, orderId, locale, paymentProvider, amount, false, transactionType);
    return {
      success: response.success,
      data: response.data[0]
    };
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0019]`, JSON.stringify(e));
    return {
      success: false
    };
  }
};
const createSurvey = async (answer: string | null, orderId: string) => {
  try {
    await NextCustomerApi.createSurvey(answer, orderId);
  } catch (e) {
    logToSentry('error', `${LOG_ERROR.convertToOrder} [CODE: 0020]`, JSON.stringify(e));
  }
};
const checkoutService = {
  fetchCheckoutAddressCollection,
  loadAddresses,
  createOrder,
  createCustomerAccountWithAddress,
  createGuestAccountWithAddress,
  getTaxReceiptUsage,
  getTaxRegime,
  getOrderDetails,
  getAvailableDeliveries,
  getTransactionMethods,
  changeDelivery,
  setCheckoutAddressCollection,
  setSelectedDigitalTaxReceiptUsageDataId,
  setSelectedTaxRegimeDataId,
  setSelectedPayment,
  clearSessionOrderId,
  setSelectedShop,
  assignCustomerToCart,
  createAddress,
  changeAddress,
  deleteAddress,
  createTransaction,
  createSurvey
};
export default checkoutService;